import { Image as ExpoImage } from 'expo-image';
import type { ImagePickerAsset } from 'expo-image-picker';
import { router, useLocalSearchParams } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { Camera, ShieldAlert, UserPlus } from 'lucide-react-native';
import { useCallback, useEffect, useState, useRef } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  Pressable,
  Share,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { Button } from '@/src/components/Button';
import { ErrorView } from '@/src/components/ErrorView';
import { IdentityHeader } from '@/src/components/IdentityHeader';
import { LoadingView } from '@/src/components/LoadingView';
import { Screen } from '@/src/components/Screen';
import { useToast } from '@/src/components/Toast';
import { useAuth } from '@/src/features/auth/use-auth';
import { CommentComposeModal } from '@/src/features/comments/CommentComposeModal';
import { GarageSection } from '@/src/features/garage/GarageCard';
import { useUserGarageVehicles } from '@/src/features/garage/use-garage';
import { getMyInviteLink } from '@/src/features/invites/invite-service';
import { PostCard } from '@/src/features/posts/PostCard';
import { PostPhotoViewerModal } from '@/src/features/posts/PostPhotoViewerModal';
import { PostVideoViewerModal } from '@/src/features/posts/PostVideoViewerModal';
import {
  PhotoViewer,
  type PhotoContext,
} from '@/src/features/posts/PhotoAttachment';
import type { PublicPost } from '@/src/features/posts/post-service';
import { useUserPosts } from '@/src/features/posts/use-user-posts';
import { ProfileImageCropperModal } from '@/src/features/profile/ProfileImageCropperModal';
import {
  pickProfileImageAsset,
  usesProfileImageCropper,
} from '@/src/features/profile/profile-image-picker';
import {
  emptyProfileReactionCounts,
  getProfileAvatarOriginalURL,
  getProfileAvatarURL,
  getProfileCoverOriginalURL,
  getProfileCoverPhotoURL,
  totalProfileReactions,
  type ProfileConnectionKind,
  type ProfileImageKind,
} from '@/src/features/profile/profile-service';
import {
  ProfileContentTabs,
  type ProfileContentTab,
} from '@/src/features/profile/ProfileContentTabs';
import { ProfileStatsSummary } from '@/src/features/profile/ProfileStatsSummary';
import {
  ProfileConnectionsModal,
  ProfileReactionBreakdownModal,
} from '@/src/features/profile/ProfileStatsModals';
import {
  useProfile,
  useProfileStats,
  useUpdateProfileImage,
} from '@/src/features/profile/use-profile';
import { trackEvent } from '@/src/lib/analytics';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { copyTextToClipboard } from '@/src/utils/clipboard';
import { formatDateOnly } from '@/src/utils/dates';
import { isShareCancelError } from '@/src/utils/errors';
import { useRefreshableListInteractions } from '@/src/utils/refreshable-list-interactions';
import { useRegisterTabScrollHandler } from '@/src/utils/tab-scroll';

type ProfileViewerImage = {
  accessibilityLabel: string;
  analyticsContext: PhotoContext;
  originalURL: string;
  thumbnailURL: string | null;
  title: string;
};

export default function ProfileScreen() {
  const params = useLocalSearchParams<{ tab?: string }>();
  const requestedTab = Array.isArray(params.tab) ? params.tab[0] : params.tab;
  const { user } = useAuth();
  const isFocused = useIsFocused();
  const { showError, showSuccess } = useToast();
  const [activeTab, setActiveTab] = useState<ProfileContentTab>(
    requestedTab === 'garage' ? 'garage' : 'posts',
  );
  const profileQuery = useProfile(user?.uid);
  const profileStatsQuery = useProfileStats(user?.uid);
  const refetchProfile = profileQuery.refetch;
  const refetchProfileStats = profileStatsQuery.refetch;
  const userPosts = useUserPosts(
    user?.uid,
    user?.uid,
    undefined,
    isFocused && activeTab === 'posts',
  );
  const garageVehicles = useUserGarageVehicles(
    user?.uid,
    isFocused && activeTab === 'garage',
  );
  const refreshUserPosts = userPosts.refresh;
  const updateProfileImage = useUpdateProfileImage(user?.uid);
  const listRef = useRef<FlatList<PublicPost>>(null);
  const [cropAsset, setCropAsset] = useState<ImagePickerAsset | null>(null);
  const [cropKind, setCropKind] = useState<ProfileImageKind | null>(null);
  const [imageUploadStatus, setImageUploadStatus] = useState<string | null>(
    null,
  );
  const [uploadingImageKind, setUploadingImageKind] =
    useState<ProfileImageKind | null>(null);
  const [commentPost, setCommentPost] = useState<PublicPost | null>(null);
  const [videoViewerPostId, setVideoViewerPostId] = useState<string | null>(
    null,
  );
  const [videoViewerInitialPost, setVideoViewerInitialPost] =
    useState<PublicPost | null>(null);
  const [photoViewerPostId, setPhotoViewerPostId] = useState<string | null>(
    null,
  );
  const [photoViewerInitialPost, setPhotoViewerInitialPost] =
    useState<PublicPost | null>(null);
  const [photoViewerInitialIndex, setPhotoViewerInitialIndex] = useState(0);
  const [copyingInviteLink, setCopyingInviteLink] = useState(false);
  const [inviteLinkURL, setInviteLinkURL] = useState<string | null>(null);
  const [connectionsKind, setConnectionsKind] =
    useState<ProfileConnectionKind | null>(null);
  const [showReactionBreakdown, setShowReactionBreakdown] = useState(false);
  const [viewerImage, setViewerImage] = useState<ProfileViewerImage | null>(
    null,
  );
  const refreshProfile = useCallback(() => {
    void refetchProfile();
    void refetchProfileStats();
    refreshUserPosts();
  }, [refetchProfile, refetchProfileStats, refreshUserPosts]);
  const listInteractions = useRefreshableListInteractions({
    listRef,
    onRefresh: refreshProfile,
    refreshing:
      profileQuery.isRefetching ||
      profileStatsQuery.isRefetching ||
      userPosts.refreshing,
  });

  useEffect(() => {
    setActiveTab(requestedTab === 'garage' ? 'garage' : 'posts');
  }, [requestedTab]);

  function changeProfileTab(nextTab: ProfileContentTab) {
    setActiveTab(nextTab);
    router.replace(nextTab === 'garage' ? '/profile?tab=garage' : '/profile');
  }

  useRegisterTabScrollHandler('profile', listInteractions.scrollToTop);

  useEffect(() => {
    let active = true;

    if (!user?.uid) {
      setInviteLinkURL(null);
      return undefined;
    }

    getMyInviteLink()
      .then((invite) => {
        if (active) {
          setInviteLinkURL(invite.url);
        }
      })
      .catch(() => {
        if (active) {
          setInviteLinkURL(null);
        }
      });

    return () => {
      active = false;
    };
  }, [user?.uid]);

  async function saveProfileImage(
    kind: ProfileImageKind,
    asset: ImagePickerAsset,
  ) {
    const imageLabel = kind === 'avatar' ? 'profile photo' : 'cover photo';

    setUploadingImageKind(kind);
    setImageUploadStatus(`Preparing ${imageLabel}...`);

    try {
      const result = await updateProfileImage.mutateAsync({
        asset,
        kind,
        onUploadProgress: (progress) => {
          setImageUploadStatus(
            `Uploading ${imageLabel} ${Math.round(progress * 100)}%`,
          );
        },
      });
      await profileQuery.refetch();
      trackEvent('profile_image_update', { image_kind: kind });
      showSuccess(
        result === 'queued'
          ? `${kind === 'avatar' ? 'Profile photo' : 'Cover photo'} will sync when your connection is back.`
          : kind === 'avatar'
            ? 'Profile photo updated.'
            : 'Cover photo updated.',
        result === 'queued' ? 'Profile queued' : 'Profile saved',
      );
    } catch (caughtError) {
      showError(
        caughtError,
        `We could not prepare that ${kind === 'avatar' ? 'profile photo' : 'cover photo'}. Please try a JPEG, PNG, WebP, or iPhone photo under 200MB.`,
      );
    } finally {
      setImageUploadStatus(null);
      setUploadingImageKind(null);
    }
  }

  async function chooseProfileImage(kind: ProfileImageKind) {
    setUploadingImageKind(kind);

    try {
      const asset = await pickProfileImageAsset(kind);

      if (!asset) {
        return;
      }

      if (usesProfileImageCropper) {
        setCropAsset(asset);
        setCropKind(kind);
        return;
      }

      await saveProfileImage(kind, asset);
    } catch (caughtError) {
      showError(
        caughtError,
        `We could not prepare that ${kind === 'avatar' ? 'profile photo' : 'cover photo'}. Please try a JPEG, PNG, WebP, or iPhone photo under 200MB.`,
      );
    } finally {
      setUploadingImageKind(null);
    }
  }

  if (profileQuery.isLoading) {
    return <LoadingView message="Loading your profile..." />;
  }

  if (profileQuery.isError) {
    return (
      <Screen>
        <ErrorView
          message={profileQuery.error.message}
          onRetry={() => {
            void profileQuery.refetch();
          }}
        />
      </Screen>
    );
  }

  const profile = profileQuery.data;
  const stats = profileStatsQuery.data ?? {
    blockedByCount: 0,
    followerCount: 0,
    followingCount: 0,
    inviteCount: 0,
    reactionCounts: emptyProfileReactionCounts(),
    totalLikeCount: 0,
  };
  const fullName = profile?.fullName || 'Frienzzi user';
  const avatarInitial = fullName.slice(0, 1).toUpperCase();
  const avatarURL = getProfileAvatarURL(profile, 'display');
  const avatarOriginalURL = getProfileAvatarOriginalURL(profile);
  const coverPhotoURL = getProfileCoverPhotoURL(profile, 'hero');
  const coverPhotoOriginalURL = getProfileCoverOriginalURL(profile);
  const profileURL = profile?.username
    ? `https://frienzzi.com/profile/${profile.username}`
    : null;
  const reactionTotal = totalProfileReactions(stats);

  async function handleShareProfileLink() {
    if (!profileURL) {
      return;
    }

    try {
      if (Platform.OS === 'web') {
        await copyTextToClipboard(profileURL);
        showSuccess('Profile link copied.', 'Ready to share');
        return;
      }

      await Share.share({
        message: profileURL,
        url: profileURL,
      });
    } catch (error) {
      if (isShareCancelError(error)) {
        return;
      }

      showError(error, 'Unable to share profile link.');
    }
  }

  async function handleCopyInviteLink() {
    setCopyingInviteLink(true);

    try {
      const inviteURL = inviteLinkURL ?? (await getMyInviteLink()).url;

      if (!inviteLinkURL) {
        setInviteLinkURL(inviteURL);
      }

      await copyTextToClipboard(inviteURL);
      showSuccess('Invite link copied.', 'Ready to share');
      trackEvent('invite_link_copy', { source: 'profile' });
    } catch (error) {
      showError(error, 'Unable to copy invite link.');
    } finally {
      setCopyingInviteLink(false);
    }
  }

  return (
    <>
      <Screen scroll={false} contentStyle={styles.feedScreenContent}>
        <FlatList
          ref={listRef}
          contentContainerStyle={styles.feedContent}
          data={activeTab === 'posts' ? userPosts.posts : []}
          keyExtractor={(post) => post.id}
          ListHeaderComponent={
            <>
              <View style={styles.hero}>
                <View style={styles.cover}>
                  {coverPhotoURL ? (
                    <Pressable
                      accessibilityLabel="Profile cover photo"
                      accessibilityRole="imagebutton"
                      onPress={() => {
                        setViewerImage({
                          accessibilityLabel: 'Profile cover photo',
                          analyticsContext: 'profile_cover',
                          originalURL: coverPhotoOriginalURL ?? coverPhotoURL,
                          thumbnailURL: coverPhotoURL,
                          title: 'Profile cover photo',
                        });
                      }}
                      style={({ pressed }) => [
                        styles.coverImage,
                        pressed ? styles.photoPressed : null,
                      ]}
                    >
                      <ExpoImage
                        accessibilityIgnoresInvertColors
                        cachePolicy="memory-disk"
                        contentFit="cover"
                        source={{ uri: coverPhotoURL }}
                        style={styles.coverImage}
                        transition={160}
                      />
                    </Pressable>
                  ) : (
                    <View style={styles.coverPlaceholder}>
                      <Text style={styles.coverPlaceholderText}>Frienzzi</Text>
                    </View>
                  )}
                  <Pressable
                    accessibilityLabel="Change cover photo"
                    accessibilityRole="button"
                    disabled={Boolean(uploadingImageKind)}
                    onPress={() => {
                      void chooseProfileImage('cover');
                    }}
                    style={({ pressed }) => [
                      styles.imageEditButton,
                      styles.coverEditButton,
                      pressed && !uploadingImageKind
                        ? styles.imageEditButtonPressed
                        : null,
                      uploadingImageKind
                        ? styles.imageEditButtonDisabled
                        : null,
                    ]}
                  >
                    {uploadingImageKind === 'cover' ? (
                      <ActivityIndicator color={colors.surface} size="small" />
                    ) : (
                      <Camera
                        color={colors.surface}
                        size={17}
                        strokeWidth={2.6}
                      />
                    )}
                  </Pressable>
                </View>
                <View style={styles.identityRow}>
                  <View style={styles.avatarFrame}>
                    {avatarURL ? (
                      <Pressable
                        accessibilityLabel="Profile photo"
                        accessibilityRole="imagebutton"
                        onPress={() => {
                          setViewerImage({
                            accessibilityLabel: 'Profile photo',
                            analyticsContext: 'profile_avatar',
                            originalURL: avatarOriginalURL ?? avatarURL,
                            thumbnailURL: avatarURL,
                            title: 'Profile photo',
                          });
                        }}
                        style={({ pressed }) => [
                          styles.avatarContent,
                          pressed ? styles.photoPressed : null,
                        ]}
                      >
                        <ExpoImage
                          cachePolicy="memory-disk"
                          contentFit="cover"
                          source={{ uri: avatarURL }}
                          style={styles.avatarImage}
                          transition={160}
                        />
                      </Pressable>
                    ) : (
                      <View style={styles.avatarContent}>
                        <Text style={styles.avatarText}>{avatarInitial}</Text>
                      </View>
                    )}
                    <Pressable
                      accessibilityLabel="Change profile photo"
                      accessibilityRole="button"
                      disabled={Boolean(uploadingImageKind)}
                      hitSlop={8}
                      onPress={() => {
                        void chooseProfileImage('avatar');
                      }}
                      style={({ pressed }) => [
                        styles.imageEditButton,
                        styles.avatarEditButton,
                        pressed && !uploadingImageKind
                          ? styles.imageEditButtonPressed
                          : null,
                        uploadingImageKind
                          ? styles.imageEditButtonDisabled
                          : null,
                      ]}
                    >
                      {uploadingImageKind === 'avatar' ? (
                        <ActivityIndicator
                          color={colors.surface}
                          size="small"
                        />
                      ) : (
                        <Camera
                          color={colors.surface}
                          size={15}
                          strokeWidth={2.6}
                        />
                      )}
                    </Pressable>
                  </View>
                  <View style={styles.identityText}>
                    <IdentityHeader
                      containerStyle={styles.profileIdentityHeader}
                      density="profile"
                      founderBadge={profile?.founderBadge === true}
                      fullName={fullName}
                      interactive={false}
                      showAvatar={false}
                      username={profile?.username}
                      verifiedBadge={
                        profile?.verification?.badge?.verified === true
                      }
                    />
                    {profile?.verification?.badge?.verified ? null : (
                      <Button
                        title="Get verified"
                        variant="secondary"
                        style={styles.verifyButton}
                        onPress={() => router.push('/verify-account')}
                      />
                    )}
                    <Text style={styles.metaText}>
                      Joined {formatDateOnly(profile?.createdAt)}
                    </Text>
                  </View>
                </View>
                <Text style={styles.bio}>
                  {profile?.bio ||
                    'Add a short bio so friends know what you are up to.'}
                </Text>
                <ProfileStatsSummary
                  loading={profileStatsQuery.isLoading}
                  primaryStats={[
                    {
                      key: 'followers',
                      label: 'Followers',
                      onPress: () => setConnectionsKind('followers'),
                      value: stats.followerCount,
                    },
                    {
                      key: 'following',
                      label: 'Following',
                      onPress: () => setConnectionsKind('following'),
                      value: stats.followingCount,
                    },
                    {
                      key: 'reactions',
                      label: 'Reactions',
                      onPress: () => setShowReactionBreakdown(true),
                      value: reactionTotal,
                    },
                  ]}
                  secondaryInfoItems={[
                    {
                      label: 'Invites',
                      description:
                        "People who joined Frienzzi from this profile's invite link.",
                    },
                    {
                      label: 'Blocked by',
                      description:
                        'Signed-in users who currently block this profile. Blocker names are never shown.',
                    },
                  ]}
                  secondaryStats={[
                    {
                      Icon: UserPlus,
                      key: 'invites',
                      label: 'Invites',
                      onPress: () => setConnectionsKind('invites'),
                      value: stats.inviteCount,
                    },
                    {
                      Icon: ShieldAlert,
                      key: 'blockedBy',
                      label: 'Blocked by',
                      value: stats.blockedByCount,
                    },
                  ]}
                />
                {profileStatsQuery.isError ? (
                  <Text style={styles.error}>
                    {profileStatsQuery.error.message}
                  </Text>
                ) : null}
                {imageUploadStatus ? (
                  <View style={styles.uploadStatus}>
                    <ActivityIndicator color={colors.primary} size="small" />
                    <Text style={styles.uploadStatusText}>
                      {imageUploadStatus}
                    </Text>
                  </View>
                ) : null}
                <Button
                  title="Edit profile settings"
                  variant="secondary"
                  onPress={() => router.push('/settings')}
                  style={styles.settingsButton}
                />
                <View style={styles.profileActionsCard}>
                  <Button
                    title="Share profile"
                    variant="secondary"
                    disabled={!profileURL}
                    style={styles.profileActionButton}
                    onPress={() => {
                      void handleShareProfileLink();
                    }}
                  />
                  <Button
                    title="Invite friends"
                    variant="secondary"
                    loading={copyingInviteLink}
                    style={styles.profileActionButton}
                    onPress={() => {
                      void handleCopyInviteLink();
                    }}
                  />
                </View>
              </View>
              <ProfileContentTabs
                activeTab={activeTab}
                onChange={changeProfileTab}
              />
              {activeTab === 'posts' &&
              (userPosts.loading || userPosts.error) ? (
                <View style={styles.postsSection}>
                  {userPosts.loading ? (
                    <Text style={styles.mutedText}>Loading your posts...</Text>
                  ) : null}
                  {userPosts.error ? (
                    <ErrorView message={userPosts.error.message} />
                  ) : null}
                </View>
              ) : null}
              {activeTab === 'garage' ? (
                <>
                  <GarageSection
                    canEdit
                    emptyText="Add vehicles you own or have owned so friends can see your builds."
                    loading={garageVehicles.loading}
                    onAdd={() => router.push('/garage-manager/create')}
                    vehicles={garageVehicles.vehicles}
                  />
                  {garageVehicles.error ? (
                    <ErrorView message={garageVehicles.error.message} />
                  ) : null}
                </>
              ) : null}
            </>
          }
          ListEmptyComponent={
            activeTab === 'posts' && !userPosts.loading && !userPosts.error ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyTitle}>No posts yet</Text>
                <Text style={styles.emptyText}>
                  Posts you create from the plus button will appear here.
                </Text>
              </View>
            ) : null
          }
          ListFooterComponent={
            activeTab === 'posts' && userPosts.loadingMore ? (
              <View style={styles.feedFooter}>
                <ActivityIndicator color={colors.primary} />
              </View>
            ) : null
          }
          onEndReached={activeTab === 'posts' ? userPosts.loadMore : undefined}
          onEndReachedThreshold={0.4}
          onRefresh={refreshProfile}
          onScroll={listInteractions.onScroll}
          refreshing={
            profileQuery.isRefetching ||
            profileStatsQuery.isRefetching ||
            userPosts.refreshing
          }
          renderItem={({ item }) => (
            <PostCard
              post={item}
              onCommentPress={setCommentPost}
              onPhotoPress={(post, photoIndex) => {
                setPhotoViewerInitialPost(post);
                setPhotoViewerPostId(post.id);
                setPhotoViewerInitialIndex(photoIndex);
              }}
              onVideoPress={(post) => {
                setVideoViewerInitialPost(post);
                setVideoViewerPostId(post.id);
              }}
            />
          )}
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={false}
          {...listInteractions.webPullToRefreshHandlers}
        />
      </Screen>
      <ProfileImageCropperModal
        asset={cropAsset}
        kind={cropKind}
        visible={Boolean(cropAsset && cropKind)}
        onCancel={() => {
          setCropAsset(null);
          setCropKind(null);
        }}
        onComplete={(asset) => {
          const kind = cropKind;

          setCropAsset(null);
          setCropKind(null);

          if (kind) {
            void saveProfileImage(kind, asset);
          }
        }}
      />
      <CommentComposeModal
        post={commentPost}
        visible={Boolean(commentPost)}
        onClose={() => setCommentPost(null)}
      />
      <PostVideoViewerModal
        key={videoViewerPostId ?? 'post-video-viewer'}
        hasMorePosts={userPosts.hasMore}
        initialPost={videoViewerInitialPost}
        initialPostId={videoViewerPostId}
        loadingMorePosts={userPosts.loadingMore}
        posts={userPosts.posts}
        visible={Boolean(videoViewerPostId)}
        onClose={() => {
          setVideoViewerPostId(null);
          setVideoViewerInitialPost(null);
        }}
        onCommentPress={setCommentPost}
        onLoadMorePosts={userPosts.loadMore}
      />
      <PostPhotoViewerModal
        key={
          photoViewerPostId
            ? `${photoViewerPostId}:${photoViewerInitialIndex}`
            : 'post-photo-viewer'
        }
        hasMorePosts={userPosts.hasMore}
        initialPhotoIndex={photoViewerInitialIndex}
        initialPost={photoViewerInitialPost}
        initialPostId={photoViewerPostId}
        loadingMorePosts={userPosts.loadingMore}
        posts={userPosts.posts}
        visible={Boolean(photoViewerPostId)}
        onClose={() => {
          setPhotoViewerPostId(null);
          setPhotoViewerInitialPost(null);
          setPhotoViewerInitialIndex(0);
        }}
        onCommentPress={setCommentPost}
        onLoadMorePosts={userPosts.loadMore}
      />
      <ProfileConnectionsModal
        currentUid={user?.uid}
        kind={connectionsKind}
        profileUid={user?.uid}
        totalCount={
          connectionsKind === 'following'
            ? stats.followingCount
            : connectionsKind === 'invites'
              ? stats.inviteCount
              : stats.followerCount
        }
        visible={Boolean(connectionsKind)}
        onClose={() => setConnectionsKind(null)}
      />
      <ProfileReactionBreakdownModal
        stats={stats}
        visible={showReactionBreakdown}
        onClose={() => setShowReactionBreakdown(false)}
      />
      {viewerImage ? (
        <PhotoViewer
          accessibilityLabel={viewerImage.accessibilityLabel}
          analyticsContext={viewerImage.analyticsContext}
          onClose={() => setViewerImage(null)}
          originalURL={viewerImage.originalURL}
          thumbnailURL={viewerImage.thumbnailURL}
          title={viewerImage.title}
          visible
        />
      ) : null}
    </>
  );
}

const styles = StyleSheet.create({
  feedScreenContent: {
    alignSelf: 'stretch',
    maxWidth: '100%',
    paddingBottom: 0,
    paddingHorizontal: 0,
    paddingTop: 0,
    width: '100%',
  },
  feedContent: {
    alignSelf: 'center',
    gap: spacing.lg,
    maxWidth: 640,
    paddingBottom: 0,
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.md,
    width: '100%',
  },
  feedFooter: {
    alignItems: 'center',
    paddingVertical: spacing.md,
  },
  hero: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
  },
  cover: {
    backgroundColor: colors.surfaceTint,
    height: 164,
    position: 'relative',
    width: '100%',
  },
  coverImage: {
    height: '100%',
    width: '100%',
  },
  photoPressed: {
    opacity: 0.9,
  },
  coverPlaceholder: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    flex: 1,
    justifyContent: 'center',
  },
  coverPlaceholderText: {
    color: colors.surface,
    fontSize: 24,
    fontWeight: '900',
  },
  identityRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: -36,
    paddingHorizontal: spacing.md,
  },
  avatarFrame: {
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderColor: colors.surface,
    borderRadius: 46,
    borderWidth: 4,
    height: 92,
    justifyContent: 'center',
    overflow: 'visible',
    position: 'relative',
    width: 92,
  },
  avatarContent: {
    alignItems: 'center',
    borderRadius: 42,
    height: '100%',
    justifyContent: 'center',
    overflow: 'hidden',
    width: '100%',
  },
  avatarImage: {
    height: '100%',
    width: '100%',
  },
  avatarText: {
    color: colors.secondary,
    fontSize: 34,
    fontWeight: '900',
  },
  identityText: {
    flex: 1,
    gap: spacing.xs,
    marginTop: 38,
  },
  profileIdentityHeader: {
    alignSelf: 'stretch',
  },
  verifyButton: {
    alignSelf: 'flex-start',
    paddingHorizontal: spacing.md,
  },
  metaText: {
    color: colors.mutedText,
    fontSize: 14,
  },
  profileActionsCard: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.sm,
    marginHorizontal: spacing.md,
    marginBottom: spacing.lg,
    marginTop: -spacing.sm,
    padding: spacing.md,
  },
  profileActionButton: {
    flex: 1,
  },
  bio: {
    color: colors.text,
    fontSize: 15,
    lineHeight: 22,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.lg,
  },
  imageEditButton: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderColor: colors.surface,
    borderRadius: 18,
    borderWidth: 2,
    height: 36,
    justifyContent: 'center',
    position: 'absolute',
    width: 36,
  },
  coverEditButton: {
    right: spacing.md,
    top: spacing.md,
  },
  avatarEditButton: {
    borderRadius: 16,
    bottom: -7,
    height: 32,
    right: -7,
    width: 32,
  },
  imageEditButtonPressed: {
    backgroundColor: colors.primaryPressed,
  },
  imageEditButtonDisabled: {
    opacity: 0.75,
  },
  uploadStatus: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.sm,
    marginHorizontal: spacing.md,
    marginTop: spacing.lg,
    padding: spacing.md,
  },
  uploadStatusText: {
    color: colors.secondary,
    fontSize: 14,
    fontWeight: '800',
  },
  settingsButton: {
    margin: spacing.md,
    marginTop: spacing.lg,
  },
  error: {
    color: colors.danger,
    fontSize: 14,
  },
  postsSection: {
    gap: spacing.md,
  },
  mutedText: {
    color: colors.mutedText,
    fontSize: 14,
  },
  emptyState: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.lg,
  },
  emptyTitle: {
    color: colors.text,
    fontSize: 17,
    fontWeight: '900',
  },
  emptyText: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
});
